import type { MenuItem, Restaurant, MenuItemDetail } from '../services/restaurantService';

/** Minimal restaurant shape needed for location/cuisine-aware SEO. */
type RestaurantForSeo = Pick<Restaurant, 'restaurantName' | 'cuisineList' | 'restaurantAddressDetails' | 'appDesc'>;

/** Partial restaurant as embedded in MenuItemDetail (no address). */
type PartialRestaurantForSeo = { restaurantName?: string; restaurantAddressDetails?: Restaurant['restaurantAddressDetails'] };

// ── Location / cuisine helpers ─────────────────────────────────────────────

function cuisineText(restaurant: RestaurantForSeo): string | null {
  const list = (restaurant.cuisineList || []).map((c) => c.name).filter(Boolean);
  return list.length ? list.join(' & ') : null;
}

function locationText(restaurant: PartialRestaurantForSeo): string | null {
  const addr = restaurant.restaurantAddressDetails;
  if (!addr) return null;
  const locality = addr.locality || (addr as any).area || null;
  const city = addr.city || null;
  if (locality && city) return `${locality}, ${city}`;
  return city || locality || null;
}

// ── Title builders ─────────────────────────────────────────────────────────

/** Builds a rich, location-aware restaurant page title (without "| Peppar" suffix). */
export function buildRestaurantPageTitle(restaurant: RestaurantForSeo): string {
  const cuisine = cuisineText(restaurant);
  const loc = locationText(restaurant);
  const base = cuisine
    ? `${restaurant.restaurantName} – ${cuisine} Restaurant`
    : `${restaurant.restaurantName} – Restaurant`;
  return loc ? `${base} in ${loc}` : base;
}

/** Builds a rich menu page title (without "| Peppar" suffix). */
export function buildMenuPageTitle(restaurant: RestaurantForSeo): string {
  const cuisine = cuisineText(restaurant);
  const loc = locationText(restaurant);
  const suffix = cuisine ? `${cuisine} Food & Prices` : 'Menu & Prices';
  const base = `${restaurant.restaurantName} Menu – ${suffix}`;
  return loc ? `${base} in ${loc}` : base;
}

/** Builds a rich menu item page title (without "| Peppar" suffix). */
export function buildMenuItemPageTitle(
  item: Pick<MenuItemDetail, 'item'>,
  restaurant?: PartialRestaurantForSeo | null
): string {
  const restName = restaurant?.restaurantName;
  const loc = restaurant ? locationText(restaurant) : null;
  if (restName && loc) return `${item.item} at ${restName} – Price & Menu in ${loc}`;
  if (restName) return `${item.item} at ${restName}`;
  return item.item;
}

// ── Description builders ───────────────────────────────────────────────────

/** Builds a rich restaurant page description. */
export function buildRestaurantPageDescription(
  restaurant: RestaurantForSeo,
  opts?: { hasDelivery?: boolean }
): string {
  const cuisine = cuisineText(restaurant);
  const loc = locationText(restaurant);
  const services = opts?.hasDelivery ? 'delivery' : 'dine-in, takeaway';

  if (cuisine && loc) {
    return `${restaurant.restaurantName} is a ${cuisine} restaurant in ${loc}. Explore the menu, location, opening hours and available ${services} options.`;
  }
  if (loc) {
    return `${restaurant.restaurantName} is a restaurant in ${loc}. Explore the menu, location, opening hours and available ${services} options.`;
  }
  return (
    restaurant.appDesc ||
    `Menu and ordering at ${restaurant.restaurantName}.`
  );
}

/** Builds a rich menu page description. */
export function buildMenuPageDescription(restaurant: RestaurantForSeo): string {
  const cuisine = cuisineText(restaurant);
  const loc = locationText(restaurant);
  if (cuisine && loc) {
    return `Explore the ${restaurant.restaurantName} menu in ${loc}. View ${cuisine} dishes, categories and prices on Peppar.`;
  }
  if (loc) {
    return `Explore the ${restaurant.restaurantName} menu in ${loc}. View dishes, categories and prices on Peppar.`;
  }
  return `Order from ${restaurant.restaurantName} on Peppar.`;
}

/** Builds a rich menu item page description. */
export function buildMenuItemPageDescription(
  item: Pick<MenuItemDetail, 'item' | 'description'>,
  restaurant?: PartialRestaurantForSeo | null
): string {
  if (item.description) return item.description;
  const restName = restaurant?.restaurantName;
  const loc = restaurant ? locationText(restaurant) : null;
  if (restName && loc) {
    return `${item.item} at ${restName} in ${loc}. View the dish description, current price and available ordering options on Peppar.`;
  }
  return `Order ${item.item} from ${restName || 'Peppar'}.`;
}

// ── Quality / indexability gates ───────────────────────────────────────────

/** Returns true when the restaurant page has enough content to index. */
export function shouldIndexRestaurantPage(
  restaurant: Restaurant,
  menuItems: MenuItem[]
): boolean {
  if (!restaurant.restaurantName) return false;
  if (!restaurant.restaurantAddressDetails?.city) return false;
  return !!(
    restaurant.appDesc ||
    (restaurant.cuisineList?.length ?? 0) > 0 ||
    menuItems.length > 0
  );
}

/** Returns true when the menu page has enough items to index. */
export function shouldIndexMenuPage(
  restaurant: Restaurant | null,
  menuItems: MenuItem[]
): boolean {
  if (!restaurant?.restaurantName) return false;
  return menuItems.length >= 3;
}

/** Returns true when a menu item page has enough content to index. */
export function shouldIndexMenuItemPage(
  item: Pick<MenuItemDetail, 'item' | 'price' | 'description' | 'images'>
): boolean {
  if (!item.item || !item.price) return false;
  const hasImage = (item.images?.length ?? 0) > 0;
  return !!(item.description || hasImage);
}
